import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { useAppDispatch, useAppSelector } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { registerNotifierCallback, unregisterNotifierCallback } from 'containers/notifier-stack/utils';
import { openInNewTab } from 'utils/commonUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { selectCommunicationsPayload } from './communicationsSlice';
import {
  CommunicationsControlOnTap,
  CommunicationsInlineAnchor,
  CommunicationsMessage,
  CommunicationsScreenKey
} from './communications.model';
import { COMMUNICATIONS_PRESENTATION } from './constants';
import {
  getActionControl,
  getCommunicationsScreenKeyFromPath,
  getInlineMessage,
  getScreenSlots,
  handleCommunicationsCta
} from './utils';
import CommunicationPopup from './components/CommunicationPopup';

const CONTROL_CTA_CALLBACK_KEY = 'communications-control-cta';

interface CommunicationsContextValue {
  screenKey: CommunicationsScreenKey | null;
  fullPage: CommunicationsMessage | null;
  strip: CommunicationsMessage | null;
  popup: CommunicationsMessage | null;
  handleCta: (message: CommunicationsMessage) => void;
  runWithControl: (screenKey: CommunicationsScreenKey, action: () => void) => void;
  isControlActive: (screenKey: CommunicationsScreenKey) => boolean;
}

const CommunicationsContext = createContext<CommunicationsContextValue | null>(null);

interface CommunicationsProviderProps {
  children: React.ReactNode;
}

export const CommunicationsProvider: FC<CommunicationsProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const payload = useAppSelector(selectCommunicationsPayload);
  const [controlDialog, setControlDialog] = useState<CommunicationsControlOnTap | null>(null);
  const guardedLocationKeyRef = useRef<string | null>(null);

  const teamId = currentTeam?.id ?? null;
  // The payload is keyed by activeTeamId, which lags one render behind a team switch.
  const teamPayload = teamId != null && payload?.teamId === teamId ? payload : null;

  // Read through a ref so guards captured in stale closures still see the latest payload.
  const teamPayloadRef = useRef(teamPayload);
  teamPayloadRef.current = teamPayload;

  const screenKey = useMemo(() => getCommunicationsScreenKeyFromPath(location.pathname), [location.pathname]);

  const { fullPage, strip, popup } = useMemo(
    () => getScreenSlots(teamPayload?.screens, screenKey),
    [teamPayload?.screens, screenKey]
  );

  const handleCta = useCallback(
    (message: CommunicationsMessage) => {
      handleCommunicationsCta({
        ctaType: message.ctaType,
        ctaAction: message.ctaAction,
        navigate,
        openExternal: openInNewTab
      });
    },
    [navigate]
  );

  const showControlToast = useCallback(
    (onTap: CommunicationsControlOnTap) => {
      const primaryMessage = onTap.lines?.[0] ?? '';
      const secondaryMessage = onTap.lines?.slice(1).join(' ') ?? '';

      if (onTap.ctaText && onTap.ctaAction) {
        registerNotifierCallback(CONTROL_CTA_CALLBACK_KEY, () => {
          handleCta(onTap);
          unregisterNotifierCallback(CONTROL_CTA_CALLBACK_KEY);
        });
      }

      dispatch(
        showNotifier({
          id: `communications-control-${onTap.id}-${onTap.changeId}`,
          message: {
            primaryMessage,
            secondaryMessage: secondaryMessage || undefined,
            actionMessage: onTap.ctaText || undefined,
            actionKey: onTap.ctaText && onTap.ctaAction ? CONTROL_CTA_CALLBACK_KEY : undefined
          },
          type: NotifierTypes.WARNING,
          showClose: true,
          duration: 6000
        })
      );
    },
    [dispatch, handleCta]
  );

  const isControlActive = useCallback(
    (key: CommunicationsScreenKey) => Boolean(getActionControl(teamPayloadRef.current?.screens, key)?.onTap),
    []
  );

  const runWithControl = useCallback(
    (key: CommunicationsScreenKey, action: () => void) => {
      const onTap = getActionControl(teamPayloadRef.current?.screens, key)?.onTap;
      if (!onTap) {
        action();
        return;
      }

      const presentation =
        onTap.presentation === COMMUNICATIONS_PRESENTATION.TOAST
          ? COMMUNICATIONS_PRESENTATION.TOAST
          : COMMUNICATIONS_PRESENTATION.DIALOG;

      if (presentation === COMMUNICATIONS_PRESENTATION.TOAST) {
        showControlToast(onTap);
        return;
      }

      setControlDialog(onTap);
    },
    [showControlToast]
  );

  // Route-level guard: covers entry points without a button guard (direct URLs, history, CTAs).
  // Checked once per history entry so a payload update mid-form doesn't eject the user;
  // the submit guards on those pages cover that case.
  useEffect(() => {
    if (!teamPayload) return;
    const guardKey = `${teamPayload.teamId}:${location.key}`;
    if (guardedLocationKeyRef.current === guardKey) return;
    guardedLocationKeyRef.current = guardKey;

    if (!screenKey || !isControlActive(screenKey)) return;

    runWithControl(screenKey, () => undefined);
    // react-router stores the in-app history index here; 0 means there is no in-app page to go back to.
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(routesPath.HOME, { replace: true });
    }
  }, [teamPayload, location.key, screenKey, isControlActive, runWithControl, navigate]);

  const handleControlDialogClose = () => {
    setControlDialog(null);
  };

  const handleControlDialogCta = () => {
    if (controlDialog) {
      handleCta(controlDialog);
    }
    setControlDialog(null);
  };

  const value = useMemo(
    () => ({
      screenKey,
      fullPage,
      strip,
      popup,
      handleCta,
      runWithControl,
      isControlActive
    }),
    [screenKey, fullPage, strip, popup, handleCta, runWithControl, isControlActive]
  );

  return (
    <CommunicationsContext.Provider value={value}>
      {children}
      {controlDialog ? (
        <CommunicationPopup
          open={true}
          message={controlDialog}
          onClose={handleControlDialogClose}
          onCtaClick={handleControlDialogCta}
        />
      ) : null}
    </CommunicationsContext.Provider>
  );
};

export const useCommunications = (): CommunicationsContextValue => {
  const context = useContext(CommunicationsContext);
  if (!context) {
    return {
      screenKey: null,
      fullPage: null,
      strip: null,
      popup: null,
      handleCta: () => undefined,
      runWithControl: (_key, action) => action(),
      isControlActive: () => false
    };
  }
  return context;
};

export const useCommunicationsControl = (screenKey: CommunicationsScreenKey) => {
  const { runWithControl, isControlActive } = useCommunications();
  const runWithControlRef = useRef(runWithControl);
  runWithControlRef.current = runWithControl;

  // Stable identity so callers can safely capture it in memoised or throttled handlers.
  const allowAction = useCallback((): boolean => {
    if (!isControlActive(screenKey)) return true;
    runWithControlRef.current(screenKey, () => undefined);
    return false;
  }, [isControlActive, screenKey]);

  return { allowAction };
};

/** Inline sentence for a named row on a screen (e.g. SOURCE_OF_FUNDS on REFUND_CONFIRM). */
export const useCommunicationsInline = (
  screenKey: CommunicationsScreenKey,
  anchor: CommunicationsInlineAnchor
): CommunicationsMessage | null => {
  const payload = useAppSelector(selectCommunicationsPayload);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamId = currentTeam?.id ?? null;
  const teamPayload = teamId != null && payload?.teamId === teamId ? payload : null;

  return useMemo(
    () => getInlineMessage(teamPayload?.screens, screenKey, anchor),
    [teamPayload?.screens, screenKey, anchor]
  );
};
